/**
 * Public Dutch digital guide — "Ontdek Cozumel".
 *
 * Opened with a single-use token link: no login. Reading and online listening
 * are unlimited; the MP3 download can be used only once (enforced server-side).
 */
import { useEffect, useRef, useState } from "react";
import { createFileRoute, notFound } from "@tanstack/react-router";
import {
  Download,
  Loader2,
  Pause,
  Volume2,
  ExternalLink,
  ShieldCheck,
  MapPin,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  COZUMEL_NL_SECTIONS,
  COZUMEL_NL_STRINGS as T,
  COZUMEL_NL_TOUR_URL,
} from "@/lib/cozumel-nl-guide";
import { openCozumelGuideLink } from "@/lib/cozumel-nl-guide.functions";

export const Route = createFileRoute("/gids/cozumel/$token")({
  loader: async ({ params }) => {
    if (!/^[a-f0-9]{32}$/.test(params.token)) throw notFound();
    const status = await openCozumelGuideLink({ data: { token: params.token } });
    if (!status.valid) throw notFound();
    return { status, token: params.token };
  },
  head: ({ params }) => {
    const description =
      "Jouw digitale reisgids voor jouw excursie naar Cozumel: het rif, snorkelen, de stranden, de Maya-geschiedenis en praktische tips — met audio in het Nederlands.";
    const shortDescription =
      "Het rif, snorkelen, de stranden, de Maya-geschiedenis en praktische tips — met audio in het Nederlands.";
    const image = "https://www.mostrandomexico.mx/og/ontdek-cozumel.jpg";
    const url = `https://www.mostrandomexico.mx/gids/cozumel/${params.token}`;
    return {
      meta: [
        { title: "Ontdek Cozumel · Digitale reisgids | Mostrando México" },
        { name: "description", content: description },
        { property: "og:title", content: "Ontdek Cozumel · Digitale reisgids" },
        { property: "og:description", content: shortDescription },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { property: "og:site_name", content: "Mostrando México" },
        { property: "og:locale", content: "nl_NL" },
        { property: "og:image", content: image },
        { property: "og:image:secure_url", content: image },
        { property: "og:image:type", content: "image/jpeg" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: "Ontdek Cozumel — digitale reisgids" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: "Ontdek Cozumel · Digitale reisgids" },
        { name: "twitter:description", content: shortDescription },
        { name: "twitter:image", content: image },
        { name: "twitter:image:alt", content: "Ontdek Cozumel — digitale reisgids" },
        { name: "robots", content: "noindex,nofollow" },
      ],
    };
  },
  notFoundComponent: () => (
    <main className="mx-auto w-full max-w-xl px-4 py-20 text-center">
      <h1 className="text-xl font-bold text-foreground">{T.revokedTitle}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{T.revokedBody}</p>
    </main>
  ),
  errorComponent: () => (
    <main className="mx-auto w-full max-w-xl px-4 py-20 text-center">
      <p className="text-sm text-muted-foreground">{T.audioError}</p>
    </main>
  ),
  component: CozumelGuidePage,
});

function CozumelGuidePage() {
  const { status, token } = Route.useLoaderData();
  const [playing, setPlaying] = useState<string | null>(null);
  const [loadingAudio, setLoadingAudio] = useState<string | null>(null);
  const [audioError, setAudioError] = useState(false);
  const [downloadUsed, setDownloadUsed] = useState(status.downloadUsed);
  const [downloading, setDownloading] = useState(false);
  const [downloadNote, setDownloadNote] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, []);

  async function toggleListen(sectionId: string) {
    setAudioError(false);
    if (playing === sectionId) {
      audioRef.current?.pause();
      setPlaying(null);
      return;
    }
    audioRef.current?.pause();
    setLoadingAudio(sectionId);
    const audio = new Audio(
      `/api/public/cozumel-nl/audio?section=${encodeURIComponent(sectionId)}&token=${encodeURIComponent(token)}`,
    );
    audioRef.current = audio;
    audio.onended = () => setPlaying(null);
    audio.onerror = () => {
      setAudioError(true);
      setLoadingAudio(null);
      setPlaying(null);
    };
    try {
      await audio.play();
      setPlaying(sectionId);
    } catch {
      setAudioError(true);
    } finally {
      setLoadingAudio(null);
    }
  }

  async function download() {
    if (downloadUsed || downloading) return;
    setDownloading(true);
    setDownloadNote(null);
    try {
      const res = await fetch(
        `/api/public/cozumel-nl/audio?mode=download&token=${encodeURIComponent(token)}`,
      );
      if (res.status === 409) {
        setDownloadUsed(true);
        return;
      }
      if (!res.ok) {
        setDownloadNote(T.audioError);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ontdek-cozumel-nl.mp3";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      setDownloadUsed(true);
      setDownloadNote(T.downloadedNow);
    } catch {
      setDownloadNote(T.audioError);
    } finally {
      setDownloading(false);
    }
  }

  if (status.revoked) {
    return (
      <main className="mx-auto w-full max-w-xl px-4 py-20 text-center">
        <h1 className="text-xl font-bold text-foreground">{T.revokedTitle}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{T.revokedBody}</p>
      </main>
    );
  }

  return (
    <main className="mx-auto w-full max-w-3xl px-4 pb-20 pt-6 sm:pt-10">
      {/* Hero */}
      <section className="overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-primary/15 via-card to-card p-6 sm:p-9">
        <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-primary">
          {T.brand} · {T.kicker}
        </p>
        <h1 className="mt-3 text-3xl font-black leading-tight text-foreground sm:text-5xl">
          {T.title}
        </h1>
        <p className="mt-3 text-base font-medium text-foreground/80 sm:text-lg">{T.subtitle}</p>
        <p className="mt-4 text-sm text-muted-foreground">{T.intro}</p>
        <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5" /> Cozumel, Quintana Roo
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Volume2 className="h-3.5 w-3.5" /> {COZUMEL_NL_SECTIONS.length} audio-onderdelen
          </span>
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5" /> Persoonlijke link
          </span>
        </div>
      </section>

      {audioError && (
        <p className="mt-4 rounded-xl border border-border bg-muted px-4 py-3 text-sm text-muted-foreground">
          {T.audioError}
        </p>
      )}

      {/* Download card */}
      <section className="mt-6 rounded-3xl border border-border bg-card p-6">
        <h2 className="text-lg font-bold text-foreground">
          {downloadUsed ? T.downloadUsedTitle : T.downloadTitle}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {downloadUsed ? T.downloadUsedBody : T.downloadBody}
        </p>
        {!downloadUsed && (
          <Button size="lg" className="mt-4 w-full sm:w-auto" onClick={() => void download()}>
            {downloading ? (
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            ) : (
              <Download className="mr-2 h-5 w-5" />
            )}
            ⬇️ {downloading ? T.downloading : T.download}
          </Button>
        )}
        {downloadNote && (
          <p className="mt-3 text-xs font-medium text-primary">{downloadNote}</p>
        )}
      </section>

      {/* Sections */}
      <div className="mt-8 space-y-5">
        {COZUMEL_NL_SECTIONS.map((s) => {
          const isPlaying = playing === s.id;
          const isLoading = loadingAudio === s.id;
          return (
            <article
              key={s.id}
              className="rounded-3xl border border-border bg-card p-6 shadow-sm"
            >
              <h2 className="flex items-center gap-2 text-xl font-bold text-foreground">
                <span aria-hidden>{s.emoji}</span> {s.title}
              </h2>
              <div className="mt-3 space-y-3 text-sm leading-relaxed text-muted-foreground">
                {s.paragraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
              {s.bullets && (
                <ul className="mt-4 grid gap-2 text-sm text-foreground/85 sm:grid-cols-2">
                  {s.bullets.map((b) => (
                    <li key={b} className="rounded-xl bg-muted px-3 py-2">
                      {b}
                    </li>
                  ))}
                </ul>
              )}
              <Button
                variant={isPlaying ? "default" : "outline"}
                size="sm"
                className="mt-5"
                onClick={() => void toggleListen(s.id)}
                aria-label={`${T.listen}: ${s.title}`}
              >
                {isLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : isPlaying ? (
                  <Pause className="mr-2 h-4 w-4" />
                ) : (
                  <Volume2 className="mr-2 h-4 w-4" />
                )}
                🔊 {isLoading ? T.listening : isPlaying ? T.pause : T.listen}
              </Button>
            </article>
          );
        })}
      </div>

      {/* Tour CTA */}
      <section className="mt-8 rounded-3xl border border-primary/30 bg-primary/5 p-6 text-center">
        <Button asChild size="lg">
          <a href={COZUMEL_NL_TOUR_URL} target="_blank" rel="noopener noreferrer">
            {T.tourCta} <ExternalLink className="ml-2 h-4 w-4" />
          </a>
        </Button>
        <p className="mt-4 text-xs text-muted-foreground">{T.footer}</p>
      </section>
    </main>
  );
}
